/**
 * @nanokart/tryon-studio-sdk
 * Size Recommendation API Service
 *
 * All calls go to the partner-facing API (https://api.nanokart.ai):
 *   GET  /api/v1/inventory/{productId}/size-chart
 *   POST /api/v1/size/recommend           (TryOn-coupled, legacy)
 *   POST /api/v1/size/recommend-from-photo (decoupled, photo-file upload)
 */

export interface SizeRecommendRequest {
  image_s3_key: string;
  height_cm: number;
  size_chart_cm: Record<string, Record<string, number>> | string;
  product_id?: string;
  sku_id?: string;
  customer_id?: string;
}

export interface SizeRecommendFromPhotoFileRequest {
  height_cm: number;
  size_chart_cm: Record<string, Record<string, number>> | string;
  product_id?: string;
  sku_id?: string;
  gender?: string;
  customer_id?: string;
}

export interface SizeRecommendResponse {
  request_id: string;
  status: string;
  recommended_size: string;
  confidence_percent: number;
  fit_notes: string;
  photo_sufficient: boolean;
  insufficiency_message: string | null;
  engine: string;
  cached: boolean;
  credits_consumed: number;
  latency_ms: number;
}

export interface SizeChartResponse {
  product_id: string;
  has_size_chart: boolean;
  size_chart_cm: Record<string, Record<string, number>> | string | null;
  gender: string;
}

export class SizeApiService {
  private apiBaseUrl: string;
  private apiKey: string;

  constructor(apiBaseUrl: string, apiKey: string) {
    this.apiBaseUrl = apiBaseUrl;
    this.apiKey = apiKey;
  }

  /**
   * Auto-fetch size chart for a product from the Nanokart backend.
   * Called when the partner's CatalogItem does not include sizeChartCm.
   */
  async getSizeChart(productId: string): Promise<SizeChartResponse> {
    const response = await fetch(
      `${this.apiBaseUrl}/api/v1/inventory/${productId}/size-chart`,
      {
        headers: {
          'X-API-Key': this.apiKey,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch size chart: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Request a size recommendation from the Nanokart backend.
   * Legacy / TryOn-coupled flow — requires image_s3_key from a completed TryOn.
   */
  async getSizeRecommendation(
    params: SizeRecommendRequest
  ): Promise<SizeRecommendResponse> {
    const response = await fetch(`${this.apiBaseUrl}/api/v1/size/recommend`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': this.apiKey,
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const msg =
        (error as { detail?: string | { message?: string } })?.detail;
      const message =
        typeof msg === 'string'
          ? msg
          : (msg as { message?: string })?.message ?? `Request failed: ${response.status}`;
      throw new Error(message);
    }

    return response.json();
  }

  /**
   * Decoupled size recommendation — upload the customer photo file and get
   * a recommendation in a single request.  No TryOn required.
   *
   * Calls POST /api/v1/size/recommend-from-photo (multipart/form-data).
   */
  async getSizeRecommendFromPhotoFile(
    file: File,
    params: SizeRecommendFromPhotoFileRequest
  ): Promise<SizeRecommendResponse> {
    const formData = new FormData();
    formData.append('human_image', file);
    formData.append('height_cm', String(params.height_cm));

    const sizeChart =
      typeof params.size_chart_cm === 'string'
        ? params.size_chart_cm
        : JSON.stringify(params.size_chart_cm);
    formData.append('size_chart_cm', sizeChart);

    if (params.product_id) formData.append('product_id', params.product_id);
    if (params.sku_id)     formData.append('sku_id',     params.sku_id);
    if (params.gender)     formData.append('gender',     params.gender);
    if (params.customer_id) formData.append('customer_id', params.customer_id);

    const response = await fetch(`${this.apiBaseUrl}/api/v1/size/recommend-from-photo`, {
      method: 'POST',
      headers: {
        // NOTE: Do NOT set Content-Type here — the browser sets the
        // multipart boundary automatically when using FormData.
        'X-API-Key': this.apiKey,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const msg =
        (error as { detail?: string | { message?: string } })?.detail;
      const message =
        typeof msg === 'string'
          ? msg
          : (msg as { message?: string })?.message ?? `Request failed: ${response.status}`;
      throw new Error(message);
    }

    return response.json();
  }
}
